import React from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../../components/NmButton';
import { formatApiDateCompact } from '../../utils/calendarPickerLocale';
import type { WorkoutSummary } from './types';
import { COMPARISON_COLORS } from './types';

function secToHMS(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

interface WorkoutSlotCardProps {
  slot: 'A' | 'B';
  workout: WorkoutSummary | null;
  onPick: () => void;
  onClear: () => void;
  hidden?: boolean;
}

/** Minimal workout slot — no athlete metrics bar (mobile PWA). */
export function WorkoutSlotCard({ slot, workout, onPick, onClear, hidden }: WorkoutSlotCardProps) {
  const { t, i18n } = useTranslation();

  if (hidden) return null;

  const color = slot === 'A' ? COMPARISON_COLORS.a : COMPARISON_COLORS.b;

  if (!workout) {
    return (
      <div className="cmp-slot cmp-slot--empty" style={{ ['--cmp-slot-color' as string]: color }}>
        <NmButton variant="secondary" size="sm" onClick={onPick}>
          {t('compare.slot.pick', { slot })}
        </NmButton>
      </div>
    );
  }

  return (
    <div className="cmp-slot" style={{ ['--cmp-slot-color' as string]: color }}>
      <div className="cmp-slot__label">{slot}</div>
      <div className="cmp-slot__title" title={workout.title}>
        {workout.title}
      </div>
      <div className="cmp-slot__meta ltr-island">
        {formatApiDateCompact(workout.workout_day, i18n.language)}
      </div>
      <div className="cmp-slot__stats">
        {workout.total_time != null && <span className="ltr-island">{secToHMS(workout.total_time)}</span>}
        {workout.distance != null && <span className="ltr-island">{(workout.distance / 1000).toFixed(1)} km</span>}
        {workout.tss_actual != null && <span className="ltr-island">{Math.round(workout.tss_actual)} TSS</span>}
      </div>
      <button type="button" className="cmp-slot__clear" onClick={onClear}>
        {t('compare.slot.clear')}
      </button>
    </div>
  );
}
